import {gsap} from "gsap";
import Breakpoint from "./parts/breakpoint";
import {debounce} from "debounce";
import EmblaCarousel from 'embla-carousel';
import animateScrollTo from 'animated-scroll-to';

declare global {
    interface Window {
        clipboardData?: any;
        onYouTubeIframeAPIReady?: Function;
    }
}
declare var ncaVars: {
    ajaxUrl: string;
    templateUrl: string;
    homeUrl: string;
    isHome: boolean;
    permalink: string;
    projIndexPerPage: number;
    projIndexPerLoad: number;
    projIndexPerLoadMobile: number;
};

(function () {
    const breakpoint = new Breakpoint();

    const projectNav = document.querySelector('.project-nav');
    const videoWraps = document.querySelectorAll<HTMLElement>('.video-player');

    initProjectNav();
    initModals();
    initShares();
    initCarousels();
    initIndex();
    initLinearGlosses();
    initPeople();
    initMaps();
    initHistoryBackLinks();

    if (videoWraps.length) {
        loadYouTubeApi().then(() => {
            initSingleVideos();
            initVideoGalleries();
        });
    }

    function initProjectNav() {
        const items = projectNav?.querySelectorAll<HTMLElement>('.project-nav__item');

        if (!items || items && items.length < 1) return;

        /*
         * The menu behaves differently if it's an index of all projects or just one project
         */
        const isSingleProject = document.querySelector('.project-nav')?.getAttribute('data-is-single-project');

        // A few global elements + settings we'll need
        const singleProjectOpenButton = document.querySelector('.project-nav-button') as HTMLElement | null;
        const header = document.querySelector('header') as HTMLElement;
        const headProjectTitleEl = document.querySelector('header h2 .project-title') as HTMLElement | null;
        const projectNavActiveClass = "project-nav-open";
        let previousScreenSize = breakpoint.value;

        // Separated this out so it's more obvious what's happening in the code
        const setHeadTitle = (content: string) => {
            if (!isSingleProject) {
                headProjectTitleEl.textContent = content;
            }
        };

        // Add custom events for open and close we can use elsewhere
        const navCloseEvent = new CustomEvent("navClose", {
            bubbles: true,
            cancelable: true,
            composed: false,
        });
        const navOpenEvent = new CustomEvent("navOpen", {
            bubbles: true,
            cancelable: true,
            composed: false,
        });

        // This sets up the menu whether we have multiple projects or just one
        // Jury's out on whether it would be more readable to treat the two instances separately
        items.forEach(item => {
            const titleOpenButton = item.querySelector('.project-nav__item-open') as HTMLElement | null;

            if (!titleOpenButton && !singleProjectOpenButton) return;

            const mobileCloseButton = item.querySelector('.project-nav__item-title .project-nav__item-close') as HTMLElement | null;
            const closeButton = item.querySelector('.project-nav__subnav .project-nav__item-close') as HTMLElement | null;
            const backToProjectsButton = item.querySelector('.project-nav__subnav-item--home') as HTMLElement | null;
            const subnav = item.querySelector('.project-nav__subnav') as HTMLElement | null;
            const subnavItems = item.querySelectorAll('.project-nav__subnav-item');
            const projectTitle = item.querySelector('.project-title')?.textContent;
            const projectTitleBack = item.querySelector('.project-nav__title-back') as HTMLElement | null;
            const subnavContentLinks = item.querySelectorAll<HTMLAnchorElement>('.project-nav__subnav-content a');

            let isOpen = false;

            const openItem = () => {
                if (isOpen) return;

                isOpen = true;

                if (breakpoint.value === "mobile") {
                    if (ncaVars.isHome) {
                        // Scroll to top on home so that animation lines up with top properly
                        window.scrollTo(0, 0);
                    }

                    new Promise<void>((res) => {
                        if (isSingleProject) {
                            res();
                        } else {
                            /*
                             * These bits only need doing if we have the full nav,
                             * otherwise we're skipping straight to the .next()
                             */

                            // Figure out how far we need to move the title up on mobile
                            const distFromTop = window.pageYOffset + item.getBoundingClientRect().top;
                            const projBackRelSize = 1 + distFromTop / projectTitleBack.offsetHeight;

                            item.style.zIndex = "10";

                            const timeline = gsap.timeline();

                            // This gives us a solid colour behind the title as it moves up
                            timeline
                                .to(projectTitleBack, {
                                    scaleY: projBackRelSize,
                                    duration: 0.2,
                                }, "start")
                                .to(titleOpenButton, {
                                    y: -1 * distFromTop,
                                    duration: 0.2,
                                }, "start")
                                .then(() => {
                                    item.classList.add("open");

                                    if (ncaVars.isHome) {
                                        // Just double check we're at the top of the page before we add
                                        // the body class that will lock the scrollability of the page
                                        window.scrollTo(0, 0);
                                    }

                                    res();
                                });
                        }
                    }).then(() => {
                        mobileCloseButton.style.display = "block";

                        document.body.classList.add(projectNavActiveClass);

                        subnav.style.opacity = "1";
                        gsap.to(subnav, {
                            yPercent: 100,
                            duration: 0.5,
                        });

                        projectNav.dispatchEvent(navOpenEvent);
                    });
                } else {
                    items.forEach(tmpItem => {
                        if (item === tmpItem) {
                            tmpItem.classList.add('open');
                        } else {
                            tmpItem.classList.add('closed');
                            tmpItem.classList.remove('open');
                        }
                    });
                    setHeadTitle(projectTitle);
                    document.body.classList.add(projectNavActiveClass);

                    if (window.scrollY > header.offsetHeight) {
                        // window.scrollTo() was buggy, so we're using a plugin
                        document.documentElement.style.scrollBehavior = 'auto';
                        animateScrollTo(header.offsetHeight - 2).then(() => {
                            document.documentElement.style.scrollBehavior = 'smooth';
                        });
                    }

                    projectNav.dispatchEvent(navOpenEvent);
                }
            };

            const closeItem = (isReset: boolean = false) => {
                if (!isOpen) return;

                if (breakpoint.value === "mobile" || isReset) {
                    gsap.to(subnav, {
                        yPercent: -10, // This allows for the extra overlap that covers the nav bar
                        duration: isReset ? 0 : 0.5,
                    }).then(() => {
                        subnav.style.opacity = "0";
                        subnav.style.transform = "translateY(0)"; // This resets translateY after allowance above

                        item.classList.remove("open");

                        new Promise<void>((res) => {
                            if (isSingleProject) {
                                res();
                            } else {
                                const timeline = gsap.timeline();

                                timeline
                                    .to(titleOpenButton, {
                                        y: 0,
                                        duration: isReset ? 0 : 0.2,
                                    }, "start")
                                    .to(projectTitleBack, {
                                        scaleY: 1,
                                        duration: isReset ? 0 : 0.2,
                                    }, "start")
                                    .then(() => {
                                        res();

                                        item.style.zIndex = "1";

                                        items.forEach(tmpItem => {
                                            tmpItem.classList.remove('closed');
                                        });
                                    });
                            }
                        }).then(() => {
                            mobileCloseButton.style.display = "none";
                            document.body.classList.remove(projectNavActiveClass);

                            isOpen = false;

                            projectNav.dispatchEvent(navCloseEvent);
                        });
                    });
                }

                if (breakpoint.value !== "mobile" || isReset) {
                    setHeadTitle("");
                    document.body.classList.remove(projectNavActiveClass);

                    items.forEach(tmpItem => {
                        tmpItem.classList.remove('open');
                        tmpItem.classList.remove('closed');
                    });

                    isOpen = false;

                    projectNav.dispatchEvent(navCloseEvent);
                }
            };

            /*
             * Top level project menu open
             */
            titleOpenButton.addEventListener('click', e => {
                if (e.target === mobileCloseButton) return;

                e.preventDefault();

                openItem();
            });

            if (singleProjectOpenButton) {
                singleProjectOpenButton.addEventListener('click', e => {
                    e.preventDefault();

                    if (isOpen) {
                        closeItem();
                    } else {
                        openItem();
                    }
                });
            }

            // If we're not on a single page, the backToProjectsButton should just close rather than navigate
            const closeButtons = !isSingleProject ? [backToProjectsButton, mobileCloseButton, closeButton] : [mobileCloseButton, closeButton];
            /*
             * Top level project menu close
             */
            closeButtons.forEach(close => {
                close.addEventListener('click', e => {
                    e.preventDefault();

                    closeItem();
                });
            });


            /*
             * Project subnav items open (only required on desktop)
             */
            subnavItems.forEach(subnavItem => {
                const open = subnavItem.querySelector('.project-nav__subnav-item-open');

                if (open) {
                    open.addEventListener('mouseover', () => {
                        subnavItems.forEach(tmpSubnavItem => {
                            if (subnavItem === tmpSubnavItem) {
                                tmpSubnavItem.classList.add('active');
                            } else {
                                tmpSubnavItem.classList.remove('active');
                            }
                        });
                    });
                }
            });

            // Close the menu if we click on a link in the subcontent and we're on the same page as the url
            subnavContentLinks.forEach(scLink => scLink.addEventListener('click', e => {
                const pageUrl = location.protocol + '//' + location.host + location.pathname;
                const target = e.target as HTMLAnchorElement;
                const urlObj = new URL(target.href);
                const url = urlObj.protocol + '//' + urlObj.host + urlObj.pathname;

                if (pageUrl === url) {
                    closeItem();
                }
            }));

            // Close the menu if we switch between mobile and desktop/tablet
            window.addEventListener('resize', debounce(() => {
                if (previousScreenSize === "mobile" && breakpoint.value !== "mobile" || previousScreenSize !== "mobile" && breakpoint.value === "mobile") {
                    closeItem(true);
                }
                previousScreenSize = breakpoint.value;
            }, 500));
        });
    }

    function initModals() {
        const wraps = document.querySelectorAll('.modal-wrap');

        if (wraps.length) {
            wraps.forEach(wrap => {
                const open = wrap.querySelector('.modal__open');
                const close = wrap.querySelector('.modal__close');

                open.addEventListener('click', () => {
                    wraps.forEach(tmpWrap => tmpWrap.classList.remove('active'));
                    wrap.classList.add('active');
                });
                close.addEventListener('click', () => {
                    wrap.classList.remove('active');
                });
                projectNav.addEventListener('navOpen', () => {
                    wrap.classList.remove('active');
                });
            });
        }
    }

    function initShares() {
        const shareLinks = document.querySelectorAll('.copy-to-clipboard');

        if (shareLinks.length) {
            shareLinks.forEach((shareLink: HTMLAnchorElement) => {
                shareLink.addEventListener('click', e => {
                    e.preventDefault();

                    copyToClipboard(shareLink.href, shareLink).then(() => {
                        shareLink.classList.add('copied');

                        setTimeout(() => {
                            shareLink.classList.remove('copied');
                        }, 1000);
                    });
                });
            });
        }
    }

    function initCarousels() {
        const carousels = document.querySelectorAll<HTMLElement>('.carousel');

        if (carousels.length) {
            carousels.forEach(carousel => {
                const embla = EmblaCarousel(carousel);
                const nextButton = carousel.querySelector('.carousel__next');
                const prevButton = carousel.querySelector('.carousel__prev');

                nextButton.addEventListener('click', () => {
                    embla.scrollNext();
                });
                prevButton.addEventListener('click', () => {
                    embla.scrollPrev();
                });

                updateCarouselClasses();

                embla.on('select', () => {
                    updateCarouselClasses();
                });

                function updateCarouselClasses() {
                    // Add classes for beginning and end of nav
                    if (embla.canScrollNext()) {
                        nextButton.classList.remove('disabled');
                    } else {
                        nextButton.classList.add('disabled');
                    }
                    if (embla.canScrollPrev()) {
                        prevButton.classList.remove('disabled');
                    } else {
                        prevButton.classList.add('disabled');
                    }
                }
            });
        }
    }

    function initVideo(wrap: HTMLElement) {
        return new Promise<YT.Player | void>(res => {
            const video = wrap.querySelector<HTMLElement>('.video');
            const videoId = video.getAttribute('data-video-id');
            const videoCover = wrap.querySelector<HTMLElement>('.video-player__cover');

            if (videoId) {
                const player = new YT.Player(video, {
                    videoId,
                    playerVars: {
                        'playsinline': 1,
                        'autohide': 1,
                        'rel': 0,
                        'iv_load_policy': 3,
                        'showinfo': 0,
                        'modestbranding': 1,
                    },
                    events: {
                        'onReady': () => {
                            videoCover.addEventListener('click', () => {
                                player.playVideo();
                            });

                            res(player);
                        },
                        'onStateChange': e => {
                            if (e.data === YT.PlayerState.UNSTARTED || e.data === YT.PlayerState.BUFFERING) {
                                wrap.classList.add('loading');
                            }
                            if (e.data === YT.PlayerState.PLAYING) {
                                wrap.classList.add('played');
                            }
                        },
                    },
                });
            } else {
                res();
            }
        });
    }

    function initSingleVideos() {
        const singleVideoWraps = document.querySelectorAll<HTMLElement>('.video-player:not(.video-player--gallery)');

        if (singleVideoWraps.length) {
            singleVideoWraps.forEach(initVideo);
        }
    }

    function initVideoGalleries() {
        const galleries = document.querySelectorAll<HTMLElement>('.video-gallery');

        if (galleries.length) {
            galleries.forEach(gallery => {
                const videosWrap = gallery.querySelector<HTMLElement>('.video-gallery__videos');
                const videos = videosWrap.querySelectorAll<HTMLElement>('.video-player--gallery');
                const titles = Array.from(videos).map(video => video.getAttribute('data-video-title'));

                const searchParams = new URLSearchParams(window.location.search);
                const urlVideoIndex = searchParams.get('videoGalleryIndex');

                Promise.all(Array.from(videos).map(initVideo)).then(players => {
                    /*
                     * Init videos carousel
                     */
                    const videoCarousel = EmblaCarousel(videosWrap);

                    /*
                     * Set up nav as control for main videos
                     */
                    const navSlideNodes = gallery.querySelectorAll<HTMLElement>('.video-gallery__nav-item');

                    navSlideNodes.forEach(slideNode => {
                        slideNode.addEventListener('click', () => {
                            const reqVideoIndex = parseInt(slideNode.getAttribute('data-video-index'));

                            handleNavVideoClick(reqVideoIndex);
                        });
                    });

                    if (urlVideoIndex) {
                        handleNavVideoClick(parseInt(urlVideoIndex), false);
                        document.documentElement.style.scrollBehavior = 'auto';
                        animateScrollTo(gallery).then(() => {
                            document.documentElement.style.scrollBehavior = 'smooth';
                        });
                    }

                    /*
                     * Make nav scrollable if there are enough videos
                     */
                    if (videos.length > 3) {
                        /*
                         * Nav carousel
                         */
                        const nav = gallery.querySelector<HTMLElement>('.video-gallery__nav');

                        const navCarousel = EmblaCarousel(nav, {slidesToScroll: 2, containScroll: "trimSnaps"});
                        const nextButton = gallery.querySelector('.video-gallery__next');
                        const prevButton = gallery.querySelector('.video-gallery__prev');

                        nextButton.addEventListener('click', () => {
                            navCarousel.scrollNext();
                        });
                        prevButton.addEventListener('click', () => {
                            navCarousel.scrollPrev();
                        });

                        updateNavClasses();

                        navCarousel.on('select', () => {
                            updateNavClasses();
                        });

                        function updateNavClasses() {
                            // Add classes for beginning and end of nav
                            if (navCarousel.canScrollNext()) {
                                nextButton.classList.remove('disabled');
                            } else {
                                nextButton.classList.add('disabled');
                            }
                            if (navCarousel.canScrollPrev()) {
                                prevButton.classList.remove('disabled');
                            } else {
                                prevButton.classList.add('disabled');
                            }
                        }
                    }

                    function handleNavVideoClick(reqVideoIndex: number, autoplay = true) {
                        videoCarousel.scrollTo(reqVideoIndex, true);

                        let reqSlideIndex = 0;

                        // Play the correct video and stop others
                        players.forEach((player, tmpIndex) => {
                            if (!player) {
                                return false;
                            }

                            if (tmpIndex === reqVideoIndex && autoplay) {
                                player.playVideo();
                            } else if (tmpIndex !== reqVideoIndex) {
                                player.pauseVideo();

                                /*
                                 * Update the title and index for each nav slide.
                                 * This is easier than adding and removing slides
                                 * that correspond to the videos not shown.
                                 */
                                navSlideNodes[reqSlideIndex].setAttribute('data-video-index', tmpIndex.toString());
                                navSlideNodes[reqSlideIndex].querySelector<HTMLElement>('.video-gallery__nav-title').textContent = titles[tmpIndex];

                                reqSlideIndex++;
                            }
                        });
                    }
                });
            });
        }
    }

    function initIndex() {
        const loadMoreBtn = document.querySelector<HTMLElement>('.index__load-more');

        if (loadMoreBtn) {
            const index = document.querySelector<HTMLElement>('.index');
            const cards = index.querySelectorAll<HTMLElement>('.index-card');
            const postCount = parseInt(index.getAttribute('data-count'));
            let showing = ncaVars.projIndexPerPage;

            if (postCount - showing > 0) {

                const loadMoreBtnListener = () => {
                    if (postCount - showing > 0) {
                        const toAdd = breakpoint.value === 'mobile' ? ncaVars.projIndexPerLoadMobile : ncaVars.projIndexPerLoad;

                        // The index required is the index after the last one
                        // that's already shown/ the last one that will be shown
                        Array.from(cards).slice(showing, showing + toAdd).forEach(card => {
                            card.classList.remove('index-card--hidden');
                        });

                        showing += toAdd;

                        if (showing >= postCount) {
                            loadMoreBtn.classList.add('index-card--hidden');
                            loadMoreBtn.removeEventListener('click', loadMoreBtnListener);
                        }
                    }
                };

                loadMoreBtn.addEventListener('click', loadMoreBtnListener);
            }
        }
    }

    function initLinearGlosses() {
        const glosses = document.querySelectorAll<HTMLElement>('.linear-gloss');

        if (glosses.length) {
            const glossGroups = groupDirectSiblings(glosses);

            let wraps: HTMLElement[] = [];
            glossGroups.forEach(glossGroup => {
                const innerEl = wrapAll(glossGroup, 'div', 'linear-gloss__wrap-inner');
                const wrapEl = wrap(innerEl, 'div', 'linear-gloss__wrap');
                wraps.push(wrapEl);
            });

            addLinearGlossClasses();

            window.addEventListener('resize', debounce(() => addLinearGlossClasses(), 500));

            function addLinearGlossClasses() {
                wraps.forEach(wrapEl => {
                    // Remove the class first so scrollWidth is accurate
                    wrapEl.classList.remove('linear-gloss--scrolls');

                    if (wrapEl.offsetWidth < wrapEl.scrollWidth) {
                        wrapEl.classList.add('linear-gloss--scrolls');
                    }
                });
            }
        }
    }

    function initPeople() {
        const peopleSections = document.querySelectorAll<HTMLElement>('.people-section');

        if (peopleSections.length) {
            setInfoHeightsAllSections();

            window.addEventListener('resize', debounce(() => setInfoHeightsAllSections(), 500));

            function setInfoHeightsAllSections() {
                if (breakpoint.value !== "mobile") {
                    peopleSections.forEach(section => {
                        const personInfos = section.querySelectorAll<HTMLElement>('.person__info');
                        const columns = getColumns();

                        for (let i = 0; i < Math.ceil(personInfos.length / columns); i++) {
                            let height = 0;
                            const infosInColumn = Array.from(personInfos).slice(i * columns, (i + 1) * columns);

                            infosInColumn.forEach(info => {
                                info.style.minHeight = '0';

                                if (info.offsetHeight > height) {
                                    height = info.offsetHeight;
                                }
                            });
                            infosInColumn.forEach(info => info.style.minHeight = `${height}px`);
                        }
                    });
                }
            }

            function getColumns() {
                return window.innerWidth > 840 ? 3 : 2;
            }
        }
    }

    function initMaps() {
        const maps = document.querySelectorAll<HTMLElement>('.map');

        if (maps.length) {
            const mapActiveBodyClass = 'map-active';
            maps.forEach(map => {
                const points = map.querySelectorAll<HTMLButtonElement>('.map__point');
                const pointInfos = map.querySelectorAll<HTMLElement>('.map__point-info');

                points.forEach((point, index) => {
                    point.addEventListener('click', () => {
                        pointInfos[index].classList.add('active');

                        document.body.classList.add(mapActiveBodyClass);
                    });
                });

                pointInfos.forEach(pointInfo => {
                    const closeButton = pointInfo.querySelector<HTMLElement>('.cross');

                    closeButton.addEventListener('click', () => {
                        pointInfo.classList.remove('active');

                        document.body.classList.remove(mapActiveBodyClass);
                    });
                });
            });
        }
    }

    function initHistoryBackLinks() {
        const links = document.querySelectorAll<HTMLAnchorElement>('.history-back-link');

        if (links.length && history) {
            links.forEach(link => {
                link.addEventListener('click', e => {
                    e.preventDefault();

                    history.back();
                });
            });
        }
    }

    function loadYouTubeApi() {
        return new Promise<void>(resolve => {
            window.onYouTubeIframeAPIReady = resolve;

            const tag = document.createElement('script');
            tag.src = "https://www.youtube.com/iframe_api";

            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        });
    }

    /*
     * copyWrap is only required if modern clipboard behaviour isn't available.
     * We have to append an element to the page and if we append it at the end we
     * will have a page jump, which is undesirable.
     */
    function copyToClipboard(content: string, copyWrap: HTMLElement) {
        // navigator.clipboard.writeText() returns a promise so we'll return a promise after the other methods
        if (navigator.clipboard) {
            return navigator.clipboard.writeText(content);
        } else if (window.clipboardData && window.clipboardData.setData) {
            // for IE11
            window.clipboardData.setData('Text', content);
            return Promise.resolve();
        } else {
            // Everything else
            const input = document.createElement('input');
            input.type = 'text';
            input.style.width = '0';
            input.style.height = '0';
            input.style.overflow = 'hidden';
            input.value = content;
            // Append to an element close to our trigger as the browser will jump to the focus
            copyWrap.append(input);
            input.focus();
            input.select();
            document.execCommand('copy');
            input.remove();
            return Promise.resolve();
        }
    }

    function groupDirectSiblings(elements: NodeListOf<HTMLElement>): Array<Array<HTMLElement>> {
        let elementGroups: Array<Array<HTMLElement>> = [];
        let elementGroup: Array<HTMLElement> = [elements[0]];

        elements.forEach((element, index) => {
            // If this is the last element, add the final node group and return
            if (index + 1 === elements.length) {
                elementGroups.push(elementGroup);

                return;
            }

            /*
             * nextSibling could be a text node or it could be text, so we want
             * to check. If it's empty then we'll take the next element node - this
             * won't be foolproof as we could have a new line then some text, but
             * WordPress mostly uses <p> tags anyway.
             */
            let nextSibling = element.nextSibling;
            if (element.nextSibling && element.nextSibling.nodeType === Node.TEXT_NODE && !element.nextSibling.textContent.trim()) {
                nextSibling = element.nextElementSibling;
            }

            /*
             * Now that we have the next node, if the next element is a directly
             * adjacent sibling, add it to current node group, otherwise add current
             * element group and start a new one with the next element.
             */
            if (nextSibling === elements[index + 1]) {
                elementGroup.push(elements[index + 1]);
            } else {
                elementGroups.push(elementGroup);
                elementGroup = [elements[index + 1]];
            }
        });

        return elementGroups;
    }

    function wrap(element: HTMLElement, wrapper: HTMLElement | string = 'div', classes: string = '') {
        if (!element) {
            return;
        }

        if (typeof wrapper === "string") {
            wrapper = document.createElement('div');
        }

        if (classes) {
            wrapper.classList.add(classes);
        }

        element.parentNode.insertBefore(wrapper, element);
        (wrapper as HTMLElement).appendChild(element);

        return wrapper;
    }

    function wrapAll(elements: NodeListOf<HTMLElement> | Array<HTMLElement>, wrapper: HTMLElement | string = 'div', classes: string = '') {
        if (!elements.length) {
            return;
        }

        if (typeof wrapper === "string") {
            wrapper = document.createElement('div');
        }

        if (classes) {
            wrapper.classList.add(classes);
        }

        elements[0].parentNode.insertBefore(wrapper, elements[0]);
        elements.forEach(element => (wrapper as HTMLElement).appendChild(element));

        return wrapper;
    }
})();